import React from "react";
import { Sparkles, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

// How many items onboarding auto-generation asks for per side (backend
// Onboarding::AutoGeneration::SCENARIO_COUNT / METRIC_COUNT).
export const AUTO_GENERATE_COUNT = 5;

// "Done for you" card for a checklist step whose items are auto-generated for
// the first agent. Deliberately styled apart from the numbered do-it-yourself
// timeline below it (tinted surface, sparkle tile, its own label) so it doesn't
// read as another substep.
const OnboardingAutoGenerationCard = ({ noun, status, count, agentName, onView }) => {
  const generating = status === "pending" || status === "running";
  const completed = status === "completed";
  const failed = status === "failed";
  if (!generating && !completed && !failed) return null;

  const total = count || AUTO_GENERATE_COUNT;
  const StatusIcon = generating ? Loader2 : completed ? CheckCircle2 : AlertCircle;

  return (
    <div
      data-testid="onboarding-autogen-card"
      className={`mb-5 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center ${
        failed ? "border-border bg-muted/40" : "border-primary/20 bg-primary/5"
      }`}
    >
      <div className="flex flex-1 items-start gap-3 min-w-0">
        <span
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
            failed ? "bg-muted text-muted-foreground" : "bg-blue-500/10 text-blue-600 dark:text-blue-400"
          }`}
        >
          <Sparkles className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <p
            className={`text-[10px] font-semibold uppercase tracking-wide ${
              failed ? "text-muted-foreground" : "text-blue-600 dark:text-blue-400"
            }`}
          >
            {generating ? "Auto-generating" : completed ? "Auto-generated" : "Auto-generation"} for{" "}
            {agentName || "your agent"}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
            <StatusIcon
              className={`h-3.5 w-3.5 flex-shrink-0 ${
                generating ? "animate-spin text-muted-foreground" : completed ? "text-emerald-500" : "text-muted-foreground"
              }`}
            />
            {generating && `Auto-generating ${AUTO_GENERATE_COUNT} ${noun}…`}
            {completed && `${total} ${noun} ready`}
            {failed && `We couldn't auto-generate ${noun}`}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {generating &&
              "Drafted from your agent's instructions — this step checks off when they're ready. You can keep going meanwhile."}
            {completed && "Drafted from your agent's instructions. Review them, or add more below."}
            {failed && "No problem — generate or write them yourself below."}
          </p>
        </div>
      </div>
      {completed && (
        <button
          onClick={onView}
          className="flex-shrink-0 self-start sm:self-auto px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
        >
          View {total} auto-generated {noun}
        </button>
      )}
    </div>
  );
};

export default OnboardingAutoGenerationCard;
